import { Button } from '@byecode/ui'
import type {
    ArgumentsSchema,
    AvailableFieldBlockInfo,
    AvailableFilterBlockInfo,
    Conditions,
    DataSourceAbstract,
    Field,
    FilterBlockAbstract,
    FilterGroupType,
    IndependentAction,
    PageVariableSchema,
    StateSchema,
    TypeInstanceMap,
    VariableADTvalue,
    ViewField,
    ViewType
} from '@lighthouse/core'
import { FilterMode, VariableType } from '@lighthouse/core'
import cls from 'classnames'
import { find } from 'rambda'
import React, { useCallback, useMemo } from 'react'
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form'

import type { FlowNode, Workflow } from '../../types'
import { getDefaultOperator, getRealField } from '../../utils'
import { DEFAULT_VALUE_VARIABLE } from '..'
import { MorePopover } from '../Condition'
import FilterCondition from './FilterCondition'
import * as SC from './styles'
import { WhereSelect } from './WhereSelect'

export interface FilterGroupProps {
    dataSource?: DataSourceAbstract
    availableFieldBlocksInfo?: AvailableFieldBlockInfo[]
    availableFilterBlocksInfo?: AvailableFilterBlockInfo[]
    dataSourceList: DataSourceAbstract[]
    workflowList?: Workflow[]
    pageVariables?: PageVariableSchema[]
    pagePathParameter?: ArgumentsSchema[]
    pageQueryParameter?: ArgumentsSchema[]
    pageStateParameter?: StateSchema[]
    upstreamNodes?: FlowNode[]
    allUpstreamNodes?: FlowNode[]
    allParentActions?: IndependentAction[]
    columns: ViewField[]
    primaryField?: string
    children?: React.ReactNode
    level?: number
    prefixName: string
    noSettingInnerType?: Set<TypeInstanceMap>
    fieldMode?: FilterMode
    paramsMode?: FilterMode
    formDsId?: string
    enableCurrentUser?: boolean
    disableUserOption?: boolean
    disableDataSourceOption?: boolean
    viewDsId?: string
    viewType?: ViewType
    extraViewFields?: Field[]
    paramsDataSource?: DataSourceAbstract
    isUseImage?: boolean
    filterBlocks?: FilterBlockAbstract[]
    enableFilterVariable?: boolean
    disabled?: boolean
    onDuplicate?: () => void
    onDelete?: () => void
    onMouseEnter?: (blockId: string) => void
    onMouseLeave?: () => void
}

export const FilterGroup: React.FC<FilterGroupProps> = ({
    dataSource,
    availableFieldBlocksInfo,
    availableFilterBlocksInfo,
    dataSourceList,
    workflowList,
    pagePathParameter,
    pageQueryParameter,
    pageVariables,
    pageStateParameter,
    upstreamNodes,
    allUpstreamNodes,
    allParentActions,
    columns,
    primaryField,
    prefixName,
    level = 0,
    noSettingInnerType,
    fieldMode = FilterMode.NORMAL,
    paramsMode,
    formDsId,
    enableCurrentUser,
    disableUserOption,
    disableDataSourceOption,
    viewDsId,
    viewType,
    extraViewFields,
    paramsDataSource,
    isUseImage,
    filterBlocks,
    enableFilterVariable,
    disabled,
    onDuplicate,
    onDelete,
    onMouseEnter,
    onMouseLeave
}) => {
    const { control, getValues } = useFormContext()
    const { fields, append, insert, remove } = useFieldArray({
        control,
        name: `${prefixName}.conditions`
    })
    const where = useWatch({ control, name: `${prefixName}.where` })
    const conditions: Conditions = useWatch({ control, name: `${prefixName}.conditions` })
    const isTopLevel = level > 0
    const isRealFilterGroup = !!where && isTopLevel
    const isMoreThanOneCondition = fields.length > 1

    const isNoneConditions = !conditions || conditions.length === 0

    const initIdVariable: VariableADTvalue = useMemo(
        () =>
            fieldMode === FilterMode.NORMAL
                ? {
                      type: VariableType.FIELD_ID,
                      fieldIdVariable: { fieldId: primaryField }
                  }
                : {
                      type: VariableType.VALUE
                  },
        [fieldMode, primaryField]
    )

    const handleAdd = useCallback(() => {
        const viewField = find(item => item.fieldId === primaryField, columns)
        if (!viewField) {
            append({ idVariable: initIdVariable, operator: '', paramList: [DEFAULT_VALUE_VARIABLE] })
            return
        }
        const field = { ...viewField, id: viewField.fieldId, name: viewField.title } as Field
        const realField = getRealField(field)
        const operator = getDefaultOperator(realField)

        append({ idVariable: initIdVariable, operator, paramList: [DEFAULT_VALUE_VARIABLE] })
    }, [append, columns, initIdVariable, primaryField])

    const handleAddGroup = useCallback(() => {
        const viewField = find(item => item.fieldId === primaryField, columns)
        if (!viewField) {
            append({
                where: 'OR',
                conditions: [{ idVariable: initIdVariable, operator: '', paramList: [DEFAULT_VALUE_VARIABLE] }]
            })
            return
        }
        const field = { ...viewField, id: viewField.fieldId, name: viewField.title } as Field
        const realField = getRealField(field)
        const operator = getDefaultOperator(realField)

        append({
            where: 'OR',
            conditions: [{ idVariable: initIdVariable, operator, paramList: [DEFAULT_VALUE_VARIABLE] }]
        })
    }, [append, columns, initIdVariable, primaryField])

    const handleDuplicateGroup = useCallback(
        (index: number) => {
            const duplicatedData = getValues(`${prefixName}.conditions[${index}]`)
            insert(index, duplicatedData)
        },
        [getValues, insert, prefixName]
    )

    const handleDeleteRole = useCallback(
        (index: number) => {
            remove(index)
        },
        [remove]
    )

    const filterList = useMemo(
        () =>
            fields.map((field, index) => (
                <SC.ListItem key={field.id}>
                    {(field as FilterGroupType)?.where ? (
                        <FilterGroup
                            pagePathParameter={pagePathParameter}
                            pageQueryParameter={pageQueryParameter}
                            pageVariables={pageVariables}
                            pageStateParameter={pageStateParameter}
                            upstreamNodes={upstreamNodes}
                            allUpstreamNodes={allUpstreamNodes}
                            allParentActions={allParentActions}
                            prefixName={`${prefixName}.conditions[${index}]`}
                            dataSource={dataSource}
                            availableFieldBlocksInfo={availableFieldBlocksInfo}
                            availableFilterBlocksInfo={availableFilterBlocksInfo}
                            dataSourceList={dataSourceList}
                            workflowList={workflowList}
                            columns={columns}
                            primaryField={primaryField}
                            fieldMode={fieldMode}
                            paramsMode={paramsMode}
                            level={level + 1}
                            enableCurrentUser={enableCurrentUser}
                            viewDsId={viewDsId}
                            viewType={viewType}
                            extraViewFields={extraViewFields}
                            paramsDataSource={paramsDataSource}
                            isUseImage={isUseImage}
                            filterBlocks={filterBlocks}
                            enableFilterVariable={enableFilterVariable}
                            disabled={disabled}
                            onDuplicate={() => {
                                handleDuplicateGroup(index)
                            }}
                            onDelete={() => {
                                handleDeleteRole(index)
                            }}
                            onMouseEnter={onMouseEnter}
                            onMouseLeave={onMouseLeave}
                        />
                    ) : (
                        <FilterCondition
                            pagePathParameter={pagePathParameter}
                            pageQueryParameter={pageQueryParameter}
                            pageVariables={pageVariables}
                            pageStateParameter={pageStateParameter}
                            upstreamNodes={upstreamNodes}
                            allUpstreamNodes={allUpstreamNodes}
                            allParentActions={allParentActions}
                            dataSource={dataSource}
                            availableFieldBlocksInfo={availableFieldBlocksInfo}
                            availableFilterBlocksInfo={availableFilterBlocksInfo}
                            dataSourceList={dataSourceList}
                            workflowList={workflowList}
                            columns={columns}
                            prefixName={`${prefixName}.conditions[${index}]`}
                            noSettingInnerType={noSettingInnerType}
                            fieldMode={fieldMode}
                            paramsMode={paramsMode}
                            enableCurrentUser={enableCurrentUser}
                            disableUserOption={disableUserOption}
                            disableDataSourceOption={disableDataSourceOption}
                            formDsId={formDsId}
                            viewDsId={viewDsId}
                            viewType={viewType}
                            extraViewFields={extraViewFields}
                            paramsDataSource={paramsDataSource}
                            isUseImage={isUseImage}
                            filterBlocks={filterBlocks}
                            enableFilterVariable={enableFilterVariable}
                            disabled={disabled}
                            onDuplicate={() => {
                                handleDuplicateGroup(index)
                            }}
                            onDelete={() => {
                                handleDeleteRole(index)
                            }}
                            onMouseEnter={onMouseEnter}
                            onMouseLeave={onMouseLeave}
                        />
                    )}
                </SC.ListItem>
            )),
        [
            fields,
            pagePathParameter,
            pageQueryParameter,
            pageVariables,
            pageStateParameter,
            upstreamNodes,
            allUpstreamNodes,
            allParentActions,
            prefixName,
            dataSource,
            availableFieldBlocksInfo,
            availableFilterBlocksInfo,
            dataSourceList,
            workflowList,
            columns,
            primaryField,
            fieldMode,
            paramsMode,
            level,
            enableCurrentUser,
            viewDsId,
            viewType,
            extraViewFields,
            paramsDataSource,
            isUseImage,
            filterBlocks,
            enableFilterVariable,
            disabled,
            onMouseEnter,
            onMouseLeave,
            noSettingInnerType,
            disableUserOption,
            disableDataSourceOption,
            formDsId,
            handleDuplicateGroup,
            handleDeleteRole
        ]
    )

    const filterListContent = useMemo(() => {
        return (
            <SC.FilterGroup>
                {isMoreThanOneCondition && (
                    <Controller
                        name={`${prefixName}.where`}
                        control={control}
                        render={({ field: ctlField }) => <WhereSelect value={ctlField.value} onChange={ctlField.onChange} />}
                    />
                )}
                <SC.FilterGroupItemWrapper>{filterList}</SC.FilterGroupItemWrapper>
            </SC.FilterGroup>
        )
    }, [control, filterList, isMoreThanOneCondition, prefixName])

    return (
        <SC.FilterGroupWrapper>
            <SC.FilterGroupContentWrapper className={cls({ 'real-filter-group': isRealFilterGroup })}>
                {!isNoneConditions && filterListContent}
                <SC.PropertyFooter>
                    <Button icon={<SC.PropertyAdderIcon fill="var(--color-gray-500)" type="Add" />} onClick={handleAdd}>
                        添加条件
                    </Button>
                    <Button icon={<SC.PropertyAdderIcon fill="var(--color-gray-500)" type="Group" />} onClick={handleAddGroup}>
                        添加条件组
                    </Button>
                </SC.PropertyFooter>
            </SC.FilterGroupContentWrapper>
            {isTopLevel && <MorePopover disabled={disabled} onDuplicate={onDuplicate} onDelete={onDelete} />}
        </SC.FilterGroupWrapper>
    )
}
